import { RegistroIngresoPrefill } from "../registroIngresoNavigation";
import { EntryRegistrationFormValues } from "./types";
import {
  determineGuestTypeFromEvent,
  isTransportCompanionEvent,
  isTransportEvent,
} from "../utils/guestTypeUtils";
import { normalizeSexValue, parseBirthDateValue } from "../utils/visitorPersonalData";
import { parseApiOrIsoDate } from "../utils/apiDates";
import { PermanencePreset } from "./types";

const parseRetentionTime = (
  timeStr: string
): { preset: PermanencePreset; custom: { days: number; hours: number; minutes: number } } => {
  if (!timeStr) {
    return { preset: "custom", custom: { days: 0, hours: 0, minutes: 0 } };
  }

  const match = timeStr.match(/(?:(\d+)d)?(?:(\d+)h)?(?:(\d+)m)?/);
  const days = match && match[1] ? parseInt(match[1], 10) : 0;
  const hours = match && match[2] ? parseInt(match[2], 10) : 0;
  const minutes = match && match[3] ? parseInt(match[3], 10) : 0;

  if (days === 0 && hours === 0 && minutes === 30)
    return { preset: "30min", custom: { days, hours, minutes } };
  if (days === 0 && hours === 1 && minutes === 0)
    return { preset: "1h", custom: { days, hours, minutes } };
  if (days === 0 && hours === 12 && minutes === 0)
    return { preset: "12h", custom: { days, hours, minutes } };
  if (days === 1 && hours === 0 && minutes === 0)
    return { preset: "24h", custom: { days, hours, minutes } };

  return { preset: "custom", custom: { days, hours, minutes } };
};

const resolveHikvisions = (
  entranceHikvisions: any[],
  globalHikvisions: Array<{ key: string; name: string }> = []
) => {
  if (!entranceHikvisions || !Array.isArray(entranceHikvisions)) return [];

  return entranceHikvisions
    .map((eventDevice: any) => {
      if (typeof eventDevice === "string") {
        const match = globalHikvisions.find((g) => g.key === eventDevice || g.name === eventDevice);
        return match ? match.key : eventDevice;
      }

      const eventId = eventDevice.securityStaff || eventDevice.id || eventDevice.key;
      const eventName = eventDevice.name;

      const match = globalHikvisions.find((g) => g.key === eventId || g.name === eventName);

      return match ? match.key : eventId;
    })
    .filter(Boolean);
};

export const buildDefaultEntryRegistrationValues = (
  prefill?: RegistroIngresoPrefill,
  globalDeviceOptions?: {
    hikvisions: Array<{ key: string; name: string }>;
    hikvisionsDefault: string[];
    retentionTimeIn: string;
    retentionTimeOut: string;
  }
): EntryRegistrationFormValues => {
  // Nunca un `Date` inválido: sobrevive hasta el submit y ahí revienta al serializarse.
  const insuranceExpirationDate = parseApiOrIsoDate(prefill?.insuranceExpirationDate);
  const vtvExpirationDate = parseApiOrIsoDate(prefill?.vtvExpirationDate);
  const birthdate = parseBirthDateValue(prefill?.birthdate);
  const entranceEvent = prefill?.entranceEvent;
  const isTransportCompanion = isTransportCompanionEvent(entranceEvent);
  const entranceAuths = entranceEvent?.authorizations?.map((a: any) => a.id || a) || [];
  // El remito lo carga el guardia al ingresar el camión y viaja en el evento de ingreso.
  const entranceReceipt = isTransportEvent(entranceEvent)
    ? String((entranceEvent.event || entranceEvent).receipt ?? "")
    : undefined;

  const rawInvites = entranceEvent?.invites || [];
  const entranceInvites: string[] = [];
  const employeeConfirmations: any[] = [];
  const employeeConfirmationsData: any[] = [];
  const entrancePrevals: string[] = (
    entranceEvent?.prevalidations ||
    entranceEvent?.prevalidation ||
    []
  ).map((p: any) => p.id || p);

  rawInvites.forEach((i: any) => {
    if (i.type === "guard") {
      const host = i.host?.id || i.host;
      // Una confirmación en guardia sin anfitrión no es una autorización que se pueda reenviar: el
      // backend rechaza el egreso al recibirla. Excepción: el acompañante del transportista, que
      // entra colgado de una autorización sin persona resoluble propia — el backend acepta
      // host: null para ese caso (ver buildExitEmployeeConfirmation / resolveTransportConfirmation-
      // Source en useEvent), así que sí se prefillea y se muestra en "Seleccionado en guardia".
      if (!host && !isTransportCompanion) return;
      employeeConfirmations.push({
        host: host || null,
        company: i.company,
        // Sin anfitrión, el responsable que autorizó el acceso y el medio por el que se consiguió
        // son lo único que respalda la confirmación: se conservan para poder reenviarlos en el
        // egreso.
        ...(!host && i.authorizedBy ? { authorizedBy: i.authorizedBy } : {}),
        ...(!host && i.communicationMethod ? { communicationMethod: i.communicationMethod } : {}),
      });
      employeeConfirmationsData.push(i);
    } else if (
      i.type === "contractor" ||
      i.type === "eventual" ||
      i.guestsType === "contractor" ||
      i.guestsType === "eventual"
    ) {
      entrancePrevals.push(i.id || i);
    } else {
      entranceInvites.push(i.id || i);
    }
  });

  return {
    id: prefill?.id ?? null,
    eventId: prefill?.eventId ?? null,
    visitorPhoto: prefill?.visitorPhoto ?? null,
    frontDNI: prefill?.frontDNI ?? null,
    backDNI: prefill?.backDNI ?? null,
    firstName: prefill?.firstName ?? "",
    lastName: prefill?.lastName ?? "",
    documentCountry: prefill?.documentCountry ?? "",
    documentNumber: prefill?.documentNumber ?? "",
    email: prefill?.email ?? "",
    phone: prefill?.phone ?? "",
    birthdate,
    sex: normalizeSexValue(prefill?.sex),
    selectedAccessMethod:
      determineGuestTypeFromEvent(prefill?.entranceEvent) ||
      prefill?.entranceEvent?.guestType ||
      ("" as any),
    accessTypeSearch: "",
    vehiclePlate: prefill?.vehiclePlate ?? "",
    vehicleType: prefill?.vehicleType ?? "none",
    vehicleModel: prefill?.vehicleModel ?? "",
    vehicleBrand: prefill?.vehicleBrand ?? "",
    vehicleColor: prefill?.vehicleColor ?? "",
    selectedVehicleId: prefill?.selectedVehicleId ?? null,
    vehicleTypeId: "",
    vehicleAppliesVtv: true,
    vehicleInsuranceCompany: "",
    vehiclePolicyNumber: "",
    vehicleSearch: "",
    enabledFacialDevices: entranceEvent?.hikvisions?.length
      ? resolveHikvisions(entranceEvent.hikvisions, globalDeviceOptions?.hikvisions || [])
      : globalDeviceOptions?.hikvisionsDefault || [],
    entryPermanenceTimePreset: parseRetentionTime(
      entranceEvent?.retentionTimeIn || globalDeviceOptions?.retentionTimeIn || ""
    ).preset,
    entryPermanenceTimeCustom: parseRetentionTime(
      entranceEvent?.retentionTimeIn || globalDeviceOptions?.retentionTimeIn || ""
    ).custom,
    exitPermanenceTimePreset: parseRetentionTime(
      entranceEvent?.retentionTimeOut || globalDeviceOptions?.retentionTimeOut || ""
    ).preset,
    exitPermanenceTimeCustom: parseRetentionTime(
      entranceEvent?.retentionTimeOut || globalDeviceOptions?.retentionTimeOut || ""
    ).custom,
    companionSelfie: null,
    companionName: "",
    companionLastName: "",
    companionDocumentCountry: "",
    companionDocumentNumber: "",
    companionEmail: "",
    companionCellphone: "",
    companionSex: "",
    companionBirthdate: null,
    companionDniFront: null,
    companionDniBack: null,
    accessVehicleBikePhoto: null,
    insuranceExpirationDate,
    insurancePhoto: prefill?.insurancePhoto ?? null,
    vtvExpirationDate,
    // El backend devuelve la VTV en `technicalReviewPhoto1`; toleramos los aliases de respuestas
    // antiguas cuando el formulario se inicia desde un prefill.
    vtvPhoto:
      prefill?.technicalReviewPhoto1 ?? prefill?.vtvPhoto ?? prefill?.technicalReviewPhoto ?? null,
    observationsText: "",
    peopleComment: prefill?.inEventPeopleComment ?? "",
    observationsPhoto: null,
    observationsPhoto2: null,
    observationsPhoto3: null,
    inEventComment: prefill?.inEventComment,
    inEventPeopleComment: prefill?.inEventPeopleComment,
    inEventPhoto: prefill?.entranceEvent?.photos?.observationsPhoto,
    inEventPhoto2: prefill?.entranceEvent?.photos?.observationsPhoto2,
    inEventPhoto3: prefill?.entranceEvent?.photos?.observationsPhoto3,
    entranceEvent: prefill?.entranceEvent,
    companions: [],
    associatedVehicles: [],
    trailerVehicles: prefill?.trailerVehicles ?? [],
    exitUnitsPreselected: prefill?.exitUnitsPreselected ?? false,
    exitOnFoot: prefill?.exitOnFoot ?? false,
    employeeConfirmations,
    employeeConfirmationsData,
    prevalidation: entrancePrevals,
    invites: entranceInvites,
    authorizations: entranceAuths,
    authorizationCompany: prefill?.company ?? "",
    passId: prefill?.passId ?? "",
    passType: prefill?.passType ?? "",
    passGuestsType: prefill?.passGuestsType ?? "",
    isBuildingMismatch: prefill?.isBuildingMismatch ?? false,
    old: {
      firstname: prefill?.firstName ?? "",
      lastname: prefill?.lastName ?? "",
      email: prefill?.email ?? "",
      phone: prefill?.phone ?? "",
      birthdate,
      // Igual que en la sesión: el baseline va crudo para que un género en formato viejo (`M`, `1`)
      // se detecte como distinto del normalizado del formulario y se persista al guardar.
      sex: prefill?.sex ?? "",
    },
    transportEmpresa: null,
    transportAutorizacion: null,
    transportAuthorizers: [],
    transportAutorizacionFull: null,
    transportFlujo: "",
    transportAutorizacionEgreso: null,
    transportLockedFields: [],
    transportAuthPrefill: null,
    transportExitControls: null,
    // Al dar salida a un camión se vuelve a mostrar el remito con el que entró. Si el guardia elige
    // una autorización de egreso con remito propio, esa lo pisa (ver applyAuthorizationPrefill).
    transportNumeroRemito: entranceReceipt,
    transportPais: "",
    transportProvincia: "",
    accessRequirementCheck: false,
    driverLegajoNumber: prefill?.transportEmployeeNumber ?? "",
    transportEmployeeNumbers: prefill?.transportEmployeeNumbers ?? [],
    driverLicenseExpirationDate: null,
    driverLicensePhoto: null,
    driverArtCompany: "",
    driverArtPolicyNumber: "",
    driverArtExpirationDate: null,
    driverArtPhoto: null,
  };
};
